/**
 * Stat Tracker Service
 * Event-driven rate tracking with a short rolling window for super-responsive
 * fish/hr and $/hr calculations. Uses time-bucketed aggregation (1-second
 * buckets) so iteration cost is O(windowSeconds × activeZones) regardless of
 * event volume — safe at millions of fish/hr.
 *
 * Records rate history every second into a ring buffer (max 1 hour) for
 * rendering live line charts in StatsView.
 *
 * Persists per-zone cumulative stats to the Zustand store.
 */

import { GameEvent, ZoneStatistics } from '@/types';
import { eventBus } from '@/services/EventBus';
import { useGameStore } from '@/state';

/** Rate data snapshot */
export interface RateSnapshot {
  fishPerHour: number;
  currencyPerHour: number;
}

/** A single point in the rate history buffer */
export interface HistoryPoint {
  timestamp: number;
  fishPerHour: number;
  currencyPerHour: number;
}

/**
 * Aggregated activity within a single second for a single zone.
 * Keeps memory bounded regardless of event throughput.
 */
interface ZoneBucket {
  fishCaught: number;
  fishSold: number;
  currencyEarned: number;
}

const DEFAULT_WINDOW_MS = 10 * 1000; // 10 seconds — instant feedback
const PRUNE_INTERVAL_MS = 5 * 1000; // Prune every 5 seconds
const MAX_HISTORY = 3600; // 1 hour at 1-second intervals

class StatTracker {
  /**
   * Outer key: second timestamp (Math.floor(Date.now() / 1000))
   * Inner key: zoneId
   */
  private buckets: Map<number, Map<string, ZoneBucket>> = new Map();
  private unsubscribers: (() => void)[] = [];
  private pruneTimer: ReturnType<typeof setInterval> | null = null;
  private windowMs: number;

  /** Global rate history, 1 snapshot per second */
  private globalHistory: HistoryPoint[] = [];
  /** Per-zone rate history */
  private zoneHistories: Map<string, HistoryPoint[]> = new Map();
  /** Timer that takes a snapshot every second */
  private historyTimer: ReturnType<typeof setInterval> | null = null;

  constructor(windowMs: number = DEFAULT_WINDOW_MS) {
    this.windowMs = windowMs;
  }

  start(): void {
    this.stop(); // Clean up any existing subscriptions

    this.unsubscribers.push(
      eventBus.on(GameEvent.FISH_CAUGHT, () => {
        const zoneId = this.getCurrentZone();
        this.addToBucket(zoneId, 'fishCaught', 1);
        this.incrementZoneStat(zoneId, 'totalFishCaught', 1);
      })
    );

    this.unsubscribers.push(
      eventBus.on(GameEvent.BUDDY_CAUGHT_FISH, () => {
        const zoneId = this.getCurrentZone();
        this.addToBucket(zoneId, 'fishCaught', 1);
        this.incrementZoneStat(zoneId, 'totalFishCaught', 1);
      })
    );

    this.unsubscribers.push(
      eventBus.on(GameEvent.FISH_SOLD, (payload) => {
        const zoneId = this.getCurrentZone();
        const fishCount = payload.fish.reduce((sum, s) => sum + s.count, 0);
        this.addToBucket(zoneId, 'fishSold', fishCount);
        this.addToBucket(zoneId, 'currencyEarned', payload.finalValue);
        this.incrementZoneStat(zoneId, 'totalFishSold', fishCount);
        this.incrementZoneStat(zoneId, 'totalCurrencyEarned', payload.finalValue);
      })
    );

    this.unsubscribers.push(
      eventBus.on(GameEvent.HAULER_SOLD_FISH, (payload) => {
        const zoneId = this.getCurrentZone();
        this.addToBucket(zoneId, 'fishSold', payload.fishCount);
        this.addToBucket(zoneId, 'currencyEarned', payload.value);
        this.incrementZoneStat(zoneId, 'totalFishSold', payload.fishCount);
        this.incrementZoneStat(zoneId, 'totalCurrencyEarned', payload.value);
      })
    );

    this.pruneTimer = setInterval(() => this.prune(), PRUNE_INTERVAL_MS);
    this.historyTimer = setInterval(() => this.recordHistory(), 1000);
  }

  stop(): void {
    for (const unsub of this.unsubscribers) {
      unsub();
    }
    this.unsubscribers = [];

    if (this.pruneTimer !== null) {
      clearInterval(this.pruneTimer);
      this.pruneTimer = null;
    }
    if (this.historyTimer !== null) {
      clearInterval(this.historyTimer);
      this.historyTimer = null;
    }
  }

  reset(): void {
    this.buckets.clear();
    this.globalHistory = [];
    this.zoneHistories.clear();
  }

  getSnapshot(zoneId?: string): RateSnapshot {
    return {
      fishPerHour: this.getFishPerHour(zoneId),
      currencyPerHour: this.getCurrencyPerHour(zoneId),
    };
  }

  getFishPerHour(zoneId?: string): number {
    const cutoffSec = Math.floor((Date.now() - this.windowMs) / 1000);
    let total = 0;
    for (const [sec, zones] of this.buckets) {
      if (sec < cutoffSec) continue;
      if (zoneId) {
        const bucket = zones.get(zoneId);
        if (bucket) total += bucket.fishCaught;
      } else {
        for (const bucket of zones.values()) {
          total += bucket.fishCaught;
        }
      }
    }
    return total / (this.windowMs / 3_600_000);
  }

  getCurrencyPerHour(zoneId?: string): number {
    const cutoffSec = Math.floor((Date.now() - this.windowMs) / 1000);
    let total = 0;
    for (const [sec, zones] of this.buckets) {
      if (sec < cutoffSec) continue;
      if (zoneId) {
        const bucket = zones.get(zoneId);
        if (bucket) total += bucket.currencyEarned;
      } else {
        for (const bucket of zones.values()) {
          total += bucket.currencyEarned;
        }
      }
    }
    return total / (this.windowMs / 3_600_000);
  }

  getActiveZones(): string[] {
    const zones = new Set<string>();
    const cutoffSec = Math.floor((Date.now() - this.windowMs) / 1000);
    for (const [sec, zoneMap] of this.buckets) {
      if (sec < cutoffSec) continue;
      for (const zoneId of zoneMap.keys()) {
        zones.add(zoneId);
      }
    }
    return Array.from(zones);
  }

  /** Returns all zone IDs that have any recorded history, even if no longer active. */
  getZonesWithHistory(): string[] {
    const zones: string[] = [];
    for (const [zoneId, history] of this.zoneHistories) {
      if (history.length > 0) {
        zones.push(zoneId);
      }
    }
    return zones;
  }

  /**
   * Get rate history for graphing.
   * @param zoneId Optional zone filter. Omit for global.
   * @param seconds How many seconds of history to return. Default: 60.
   */
  getHistory(zoneId?: string, seconds: number = 60): HistoryPoint[] {
    const source = zoneId
      ? (this.zoneHistories.get(zoneId) ?? [])
      : this.globalHistory;

    if (seconds >= source.length) {
      return [...source];
    }
    return source.slice(-seconds);
  }

  // ── Internal helpers ──────────────────────────────────

  private recordHistory(): void {
    const now = Date.now();

    // Snapshot global rates
    const globalSnap = this.getSnapshot();
    this.pushHistory(this.globalHistory, {
      timestamp: now,
      fishPerHour: globalSnap.fishPerHour,
      currencyPerHour: globalSnap.currencyPerHour,
    });

    // Snapshot all zones: both currently active AND those with existing history.
    // This ensures we capture the smooth decline to 0 after activity stops.
    const zonesToRecord = new Set([
      ...this.getActiveZones(),
      ...this.zoneHistories.keys(),
    ]);

    for (const zoneId of zonesToRecord) {
      if (!this.zoneHistories.has(zoneId)) {
        this.zoneHistories.set(zoneId, []);
      }
      const zoneSnap = this.getSnapshot(zoneId);
      this.pushHistory(this.zoneHistories.get(zoneId)!, {
        timestamp: now,
        fishPerHour: zoneSnap.fishPerHour,
        currencyPerHour: zoneSnap.currencyPerHour,
      });
    }
  }

  private pushHistory(buffer: HistoryPoint[], point: HistoryPoint): void {
    buffer.push(point);
    while (buffer.length > MAX_HISTORY) {
      buffer.shift();
    }
  }

  private addToBucket(
    zoneId: string,
    field: keyof ZoneBucket,
    amount: number
  ): void {
    const sec = Math.floor(Date.now() / 1000);
    if (!this.buckets.has(sec)) {
      this.buckets.set(sec, new Map());
    }
    const zoneMap = this.buckets.get(sec)!;
    if (!zoneMap.has(zoneId)) {
      zoneMap.set(zoneId, { fishCaught: 0, fishSold: 0, currencyEarned: 0 });
    }
    zoneMap.get(zoneId)![field] += amount;
  }

  private getCurrentZone(): string {
    return useGameStore.getState().playerState.currentZone;
  }

  private prune(): void {
    const cutoffSec = Math.floor((Date.now() - this.windowMs) / 1000);
    for (const sec of this.buckets.keys()) {
      if (sec < cutoffSec) {
        this.buckets.delete(sec);
      }
    }
  }

  private incrementZoneStat(
    zoneId: string,
    stat: keyof ZoneStatistics,
    amount: number
  ): void {
    useGameStore.setState((draft) => {
      if (!draft.playerState.stats.statsByZone[zoneId]) {
        draft.playerState.stats.statsByZone[zoneId] = {
          totalFishCaught: 0,
          totalFishSold: 0,
          totalCurrencyEarned: 0,
        };
      }
      draft.playerState.stats.statsByZone[zoneId][stat] += amount;
    });
  }
}

export const statTracker = new StatTracker();
